// Разбивка на непокрития излишък по отчетите-причинители (дата + количество) с
// бутон за компенсиране със ЗАДНА ДАТА (датата на най-стария отчет). Отваря се
// при клик на ред в списъка „Излишък от продажби".
//
// Сметката е същата като в списъка: цената е нето → Нето = количество × цена,
// Бруто по регистрацията по ДДС (нерегистрирана фирма → бруто = нето).
import { Modal } from "antd";
import { CloseOutlined, PlusOutlined, RiseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../hooks/useViewport";
import { modalStyles } from "../../../../utils/modalStyles";
import { formatQty } from "../../components/qty";
import { ROW_HEADER, ROW_HEADER_TEXT, ROW_READ, ROW_TOTAL } from "../../components/list/density";
import {
  formatDate,
  formatMoney,
  formatUnitPrice,
  type ShortageRow,
} from "./shortageShared";

type Props = {
  open: boolean;
  onClose: () => void;
  row: ShortageRow | null;
  onCompensate: () => void;
};

// Продукт · Количество · Ед. цена · Нето · Бруто
const GRID_CLASS =
  "grid grid-cols-[1fr_120px_110px_110px_110px] items-center gap-2.5 px-3.5";

export const ShortageBreakdownModal = ({
  open,
  onClose,
  row,
  onCompensate,
}: Props) => {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isTablet } = useViewport();
  const width = isMobile ? undefined : isTablet ? 720 : 820;

  const price = row?.lastDeliveryPrice ?? null;
  const unit = row?.unit ?? "";

  // Нето/Бруто за дадено количество (при непозната цена — тирета).
  const valuesOf = (quantity: number) => {
    if (price == null) return { net: null, gross: null };
    const net = quantity * price;
    return { net, gross: net * (row?.grossFactor ?? 1) };
  };
  const totals = valuesOf(row?.quantity ?? 0);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div className="flex max-h-[92vh] flex-col bg-surface [font-family:system-ui]">
        {/* Хедър: продукт + склад */}
        <div
          className={`flex shrink-0 items-start justify-between gap-3 border-b border-shade-2 ${
            isMobile ? "px-4 py-3.5" : "px-[22px] py-[18px]"
          }`}
        >
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-danger-tint text-danger-ink">
              <RiseOutlined className="text-xl" />
            </span>
            <div className="min-w-0">
              <div className="truncate text-[18px] font-bold leading-[1.2] text-ink">
                {row?.productName ?? ""}
              </div>
              <div className="mt-1 text-[13px] leading-[normal] text-muted">
                {t("inventoryPage.colWarehouse")}: {row?.warehouseName ?? "—"} ·{" "}
                {t("inventoryPage.shortageBreakdownSubtitle")}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-[30px] w-[30px] shrink-0 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-muted hover:bg-shade-1"
          >
            <CloseOutlined className="text-sm" />
          </button>
        </div>

        {/* Тяло: ред за всеки отчет-причинител */}
        <div
          className={`min-h-0 overflow-y-auto ${isMobile ? "p-4" : "px-[22px] py-[18px]"}`}
        >
          <div className="overflow-x-auto rounded-xl border border-shade-2">
            <div className="min-w-[560px]">
              <div className={`${GRID_CLASS} ${ROW_HEADER} border-b border-shade-2 bg-panel`}>
                {[
                  t("inventoryPage.colDate"),
                  t("inventoryPage.colDeficitQty"),
                  t("inventoryPage.colUnitPrice"),
                  t("inventoryPage.colNet"),
                  t("inventoryPage.colGross"),
                ].map((label, index) => (
                  <div
                    key={label}
                    className={`${ROW_HEADER_TEXT} ${index === 0 ? "text-left" : "text-right"}`}
                  >
                    {label}
                  </div>
                ))}
              </div>

              {(row?.breakdown ?? []).map((entry) => {
                const values = valuesOf(entry.quantity);
                return (
                  <div
                    key={entry.movementId}
                    className={`${GRID_CLASS} ${ROW_READ} border-b border-shade-1`}
                  >
                    <span className="font-[ui-monospace,Menlo,monospace] text-[13px] leading-[normal] text-ink-2">
                      {formatDate(entry.date)}
                    </span>
                    <span className="whitespace-nowrap text-right font-[ui-monospace,Menlo,monospace] text-[13px] font-bold leading-[normal] text-danger-ink">
                      -{formatQty(entry.quantity, row?.decimals)}
                      {unit ? (
                        <span className="font-normal text-danger-ink/50"> {unit}</span>
                      ) : null}
                    </span>
                    <span className="text-right font-[ui-monospace,Menlo,monospace] text-[13px] leading-[normal] text-ink-2">
                      {/* ед. цената НЕ се реже до 2 знака (до 4, когато носят стойност) */}
                      {price != null ? formatUnitPrice(price) : "—"}
                    </span>
                    <span className="text-right font-[ui-monospace,Menlo,monospace] text-[13px] leading-[normal] text-ink-2">
                      {values.net != null ? formatMoney(values.net) : "—"}
                    </span>
                    <span className="text-right font-[ui-monospace,Menlo,monospace] text-[13px] font-semibold leading-[normal] text-brand-deep-ink">
                      {values.gross != null ? formatMoney(values.gross) : "—"}
                    </span>
                  </div>
                );
              })}

              {/* Общо */}
              <div className={`${GRID_CLASS} ${ROW_TOTAL} bg-panel`}>
                <span className={ROW_HEADER_TEXT}>
                  {t("inventoryPage.total")}
                </span>
                <span className="whitespace-nowrap text-right font-[ui-monospace,Menlo,monospace] text-[13.5px] font-bold leading-[normal] text-danger-ink">
                  -{formatQty(row?.quantity ?? 0, row?.decimals)}
                  {unit ? (
                    <span className="font-normal text-danger-ink/50"> {unit}</span>
                  ) : null}
                </span>
                <span />
                <span className="text-right font-[ui-monospace,Menlo,monospace] text-[13.5px] font-bold leading-[normal] text-ink">
                  {totals.net != null ? formatMoney(totals.net) : "—"}
                </span>
                <span className="text-right font-[ui-monospace,Menlo,monospace] text-[13.5px] font-bold leading-[normal] text-brand-deep-ink">
                  {totals.gross != null ? formatMoney(totals.gross) : "—"}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-3.5 text-[12px] leading-relaxed text-muted">
            {t("inventoryPage.shortageBreakdownHint")}
          </div>
        </div>

        {/* Футър: Отказ + компенсиране със задна дата */}
        <div
          className={`flex shrink-0 justify-end gap-2.5 border-t border-shade-2 ${
            isMobile ? "flex-col-reverse gap-2" : "flex-row"
          } ${isNarrow ? "px-4 py-3" : "px-[22px] py-3.5"}`}
        >
          {/* Ghost „Отказ": ТУК фонът е бял, затова бутонът е бял с рамка. Топлият
              `#f5f2ec` от футъра на работния лист изглежда тъмен върху бяло. */}
          <button
            type="button"
            onClick={onClose}
            className={`h-11 cursor-pointer rounded-[10px] border border-shade-3 bg-surface px-[18px] text-[13.5px] font-semibold leading-[normal] text-body hover:bg-fill ${
              isMobile ? "w-full" : ""
            }`}
          >
            {t("inventoryPage.cancel")}
          </button>
          <button
            type="button"
            onClick={onCompensate}
            className={`flex h-11 cursor-pointer items-center justify-center gap-2 rounded-[10px] border-none bg-brand px-6 text-[13.5px] font-bold leading-[normal] text-white hover:bg-brand-deep ${
              isMobile ? "w-full" : ""
            }`}
          >
            <PlusOutlined className="text-[13px]" />
            {t("inventoryPage.compensateBackdated")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ShortageBreakdownModal;
